import React, { useState } from "react";
import { Folder, SaveAll, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  MenubarSub,
  MenubarSubTrigger,
  MenubarSubContent,
  MenubarItem,
  MenubarSeparator,
} from "@/components/ui/menubar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import JourneyMenuItem from "./JourneyMenuItem";
import { SavedJourney } from "@/utils/storage/types";
import { useIsMobile } from '@/hooks/use-mobile';
import { TruncatedText } from "@/components/ui/truncated-text";

interface FolderMenuItemProps {
  folder: {
    id: string;
    name: string;
  };
  folderJourneys: SavedJourney[];
  handleSaveToFolder: (folderId: string) => void;
  handleLoad: (id: string) => void;
  handleMoveToFolder: (journeyId: string, folderId: string | null) => void;
  folders: Array<{ id: string; name: string; }>;
}

const FolderMenuItem: React.FC<FolderMenuItemProps> = ({
  folder,
  folderJourneys,
  handleSaveToFolder,
  handleLoad,
  handleMoveToFolder,
  folders,
}) => {
  const isMobile = useIsMobile();
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false);

  // Mobile folder content rendered in a dialog
  const MobileDialogContent = ({ 
    folder, 
    folderJourneys, 
    handleSaveToFolder, 
    handleLoad, 
    setIsMobileDialogOpen 
  }: { 
    folder: { id: string; name: string; };
    folderJourneys: SavedJourney[];
    handleSaveToFolder: (folderId: string) => void;
    handleLoad: (id: string) => void;
    setIsMobileDialogOpen: (open: boolean) => void;
  }) => (
    <div className="space-y-2">
      <div 
        className="flex items-center justify-between p-2 rounded-md bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 cursor-pointer"
        onClick={() => {
          handleSaveToFolder(folder.id);
          setIsMobileDialogOpen(false);
        }}
      >
        <div className="flex items-center min-w-0 flex-1">
          <SaveAll className="mr-1.5 h-4 w-4 flex-shrink-0" />
          <span className="truncate">Save to this folder</span>
        </div>
      </div>
      
      <div className="mt-4">
        {folderJourneys.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            No journey maps in this folder
          </div>
        ) : (
          <div className="space-y-1">
            {folderJourneys.map((journey) => (
              <div 
                key={journey.id}
                className="flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => {
                  handleLoad(journey.id);
                  setIsMobileDialogOpen(false);
                }}
              >
                <div className="min-w-0 flex-1">
                  <TruncatedText 
                    text={journey.name} 
                    className="font-medium" 
                    tooltipSide="bottom"
                    maxWidth="[180px]"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // For mobile, show a dialog
  if (isMobile) {
    return (
      <>
        <div 
          className="flex items-center justify-between p-2 rounded-md hover:bg-blue-200/50 cursor-pointer"
          onClick={() => setIsMobileDialogOpen(true)}
        >
          <div className="flex items-center min-w-0 flex-1">
            <Folder className="mr-1.5 h-4 w-4 flex-shrink-0" />
            <TruncatedText 
              text={folder.name} 
              maxWidth="[120px]" 
              tooltipSide="right"
            />
          </div>
          <ChevronRight className="h-4 w-4 ml-1 flex-shrink-0" />
        </div>

        <Dialog open={isMobileDialogOpen} onOpenChange={setIsMobileDialogOpen}>
          <DialogContent className="sm:max-w-[425px] w-[calc(100vw-40px)] max-w-[320px]">
            <DialogHeader>
              <DialogTitle className="truncate max-w-[240px] pr-6">{folder.name}</DialogTitle>
              <DialogDescription>
                Manage journey maps
              </DialogDescription>
            </DialogHeader>
            <MobileDialogContent 
              folder={folder}
              folderJourneys={folderJourneys}
              handleSaveToFolder={handleSaveToFolder}
              handleLoad={handleLoad}
              setIsMobileDialogOpen={setIsMobileDialogOpen}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // For desktop, use the regular submenu
  return (
    <MenubarSub key={folder.id}>
      <MenubarSubTrigger className="group flex items-center justify-between hover:bg-blue-200/50 py-2 px-3">
        <div className="flex items-center min-w-0 flex-1">
          <Folder className="mr-1.5 h-4 w-4 flex-shrink-0" />
          <TruncatedText 
            text={folder.name} 
            maxWidth="[140px]" 
            tooltipSide="right"
          />
        </div>
      </MenubarSubTrigger>
      <MenubarSubContent className="min-w-[220px] max-w-[280px]">
        <MenubarItem onClick={() => handleSaveToFolder(folder.id)} className="hover:bg-blue-200/50 py-2">
          <SaveAll className="mr-1.5 h-4 w-4 flex-shrink-0" />
          <span className="truncate">Save to this folder</span>
        </MenubarItem>
        
        <MenubarSeparator />
        
        {folderJourneys.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            No journey maps in this folder
          </div>
        ) : (
          folderJourneys.map((journey) => (
            <JourneyMenuItem 
              key={journey.id}
              journey={journey}
              onLoad={handleLoad}
              onMove={handleMoveToFolder}
              folders={folders}
              showFolderInfo={false}
            />
          ))
        )}
      </MenubarSubContent>
    </MenubarSub>
  );
};

export default FolderMenuItem;
